/**
 * Renders the Durga Puja timetable into a branded PNG and hands it to the
 * device share sheet (WhatsApp, etc.).
 *
 * The image is drawn on a canvas rather than screenshotting the DOM so the
 * output is a fixed, poster-quality graphic carrying SORRBA branding — logo,
 * name, venue and website — regardless of the screen it was shared from.
 *
 * Layout is one 1080px column: header medallion, one card per puja *day*
 * (tithis that fall on the same date share a card, so the four Sandhi Puja
 * moments read as one Monday rather than four repeated dates), the Dashami
 * ritual note, the venue, then the footer.
 */
import logoSrc from "assets/img/now-logo.png";

import {
  PUJA_DASHAMI_RITUALS,
  PUJA_TIMETABLE,
  PUJA_TIMETABLE_BN_TITLE,
  PUJA_YEAR,
} from "utils/pujaSchedule";
import { VENUE_ADDRESS, VENUE_MAP_URL, VENUE_NAME } from "utils/venue";

const W = 1080;
const PAD = 64;

const HEADER_H = 704;
const CARD_X = PAD;
const CARD_W = W - PAD * 2;
const CARD_GAP = 20;
const CARD_R = 28;

const TILE = 118;
const TILE_M = 26;
const COL_X = CARD_X + TILE_M + TILE + 34;
const COL_W = CARD_X + CARD_W - 34 - COL_X;
const DAY_LABEL_H = 42;
const ENTRY_H = 78;

const FOOTER_H = 196;

const INK_TOP = "#3A0F22";
const INK_MID = "#6B1E14";
const INK_BOT = "#280A12";
const GOLD = "#E8B45C";
const GOLD_BRIGHT = "#F8E3B0";
const IVORY = "#FDF6EC";

const SERIF = '"Poppins", "Segoe UI", Roboto, Arial, sans-serif';
const SANS = '"Inter", "Segoe UI", Roboto, Arial, sans-serif';

const FILE_NAME = `sorrba-durga-puja-${PUJA_YEAR}-timetable.png`;

/* The address lines are plain text so they stay readable in the chat, and the
   maps link is on its own line so WhatsApp keeps it tappable — one tap opens
   Google Maps with navigation to the pandal. */
const SHARE_TEXT = [
  `🪔 Durga Puja ${PUJA_YEAR} — SORRBA`,
  "Date & time table attached.",
  "",
  `📍 ${VENUE_NAME}`,
  VENUE_ADDRESS,
  `Navigate: ${VENUE_MAP_URL}`,
  "",
  "Celebrating 18 years of togetherness · https://www.sorrba.org",
].join("\n");

/* ---------- canvas helpers ---------- */

function roundRect(ctx, x, y, w, h, r) {
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line.length > 0 ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line.length > 0) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line.length > 0) {
    lines.push(line);
  }
  return lines;
}

/* Canvas has no portable letter-spacing (ctx.letterSpacing is Chrome-only), so
   tracked caps — the small labels that carry most of the poster's polish — are
   drawn a glyph at a time. */
function trackedWidth(ctx, text, spacing) {
  if (text.length === 0) return 0;
  let w = -spacing;
  for (const ch of text) {
    w += ctx.measureText(ch).width + spacing;
  }
  return w;
}

function drawTracked(ctx, text, x, y, spacing, align) {
  const total = trackedWidth(ctx, text, spacing);
  const prev = ctx.textAlign;
  let cx = x;
  if (align === "right") cx = x - total;
  if (align === "center") cx = x - total / 2;
  ctx.textAlign = "left";
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
  ctx.textAlign = prev;
  return total;
}

function glow(ctx, x, y, r, color) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

/* ---------- data shaping ---------- */

function splitDate(date) {
  const parts = date.split(" ");
  return {
    day: parts[0] || "",
    month: (parts[1] || "").slice(0, 3).toUpperCase(),
  };
}

const TIME_RE = /^(\d{1,2}:\d{2}\s*[ap]\.?m\.?)\s*(.*)$/i;

function splitTime(time) {
  const m = TIME_RE.exec(time.trim());
  if (m == null) return { clock: time, part: "" };
  return { clock: m[1], part: m[2] };
}

function groupByDate(rows) {
  const groups = [];
  rows.forEach((row) => {
    const last = groups[groups.length - 1];
    if (last != null && last.date === row.date) {
      last.entries.push(row);
    } else {
      groups.push({ date: row.date, day: row.day, entries: [row] });
    }
  });
  groups.forEach((group) => {
    const content = DAY_LABEL_H + group.entries.length * ENTRY_H;
    group.h = TILE_M * 2 + Math.max(TILE, content);
  });
  return groups;
}

/* ---------- drawing ---------- */

function paintBackground(ctx, H) {
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, INK_TOP);
  bg.addColorStop(0.42, INK_MID);
  bg.addColorStop(1, INK_BOT);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  glow(ctx, W / 2, 230, 560, "rgba(232,180,92,.24)");
  glow(ctx, 90, H - 300, 520, "rgba(255,120,60,.12)");
  glow(ctx, W - 60, H * 0.62, 460, "rgba(120,40,180,.10)");

  // Vignette keeps the eye on the column.
  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.28, W / 2, H / 2, H * 0.72);
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(0,0,0,.38)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  roundRect(ctx, 20, 20, W - 40, H - 40, 34);
  ctx.strokeStyle = "rgba(232,180,92,.45)";
  ctx.lineWidth = 3;
  ctx.stroke();

  roundRect(ctx, 32, 32, W - 64, H - 64, 26);
  ctx.strokeStyle = "rgba(232,180,92,.16)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawMandala(ctx, cx, cy) {
  ctx.save();
  ctx.strokeStyle = "rgba(232,180,92,.20)";
  ctx.lineWidth = 1.5;
  [148, 178, 206].forEach((r, i) => {
    ctx.globalAlpha = 1 - i * 0.28;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
  ctx.strokeStyle = "rgba(232,180,92,.34)";
  ctx.lineWidth = 2.5;
  for (let i = 0; i < 48; i += 1) {
    const a = (i / 48) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * 216, cy + Math.sin(a) * 216);
    ctx.lineTo(cx + Math.cos(a) * 228, cy + Math.sin(a) * 228);
    ctx.stroke();
  }
  ctx.restore();
}

function drawDiamondRule(ctx, cx, y, half) {
  ctx.save();
  ctx.strokeStyle = "rgba(232,180,92,.55)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - half, y);
  ctx.lineTo(cx - 18, y);
  ctx.moveTo(cx + 18, y);
  ctx.lineTo(cx + half, y);
  ctx.stroke();
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.moveTo(cx, y - 8);
  ctx.lineTo(cx + 8, y);
  ctx.lineTo(cx, y + 8);
  ctx.lineTo(cx - 8, y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawPin(ctx, cx, top, s, color) {
  const r = s * 0.32;
  const cy = top + r;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI * 0.78, Math.PI * 0.22, false);
  ctx.lineTo(cx, top + s);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.34, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawHeader(ctx, logo) {
  const cx = W / 2;
  const cy = 232;

  drawMandala(ctx, cx, cy);

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.45)";
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 10;
  ctx.beginPath();
  ctx.arc(cx, cy, 100, 0, Math.PI * 2);
  // White, not ivory: the logo PNG carries a white background, and any other
  // fill leaves a visible square inside the medallion.
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.arc(cx, cy, 100, 0, Math.PI * 2);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 112, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(232,180,92,.38)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  if (logo != null) {
    const h = 132;
    const w = (logo.width / logo.height) * h;
    ctx.drawImage(logo, cx - w / 2, cy - h / 2, w, h);
  }

  ctx.textAlign = "center";
  ctx.fillStyle = GOLD_BRIGHT;
  ctx.font = `700 66px ${SERIF}`;
  drawTracked(ctx, "SORRBA", cx, 412, 10, "center");

  drawDiamondRule(ctx, cx, 446, 170);

  ctx.fillStyle = IVORY;
  ctx.font = `600 52px ${SERIF}`;
  ctx.fillText(`Durga Puja ${PUJA_YEAR}`, cx, 514);

  ctx.fillStyle = "rgba(248,227,176,.78)";
  ctx.font = `400 30px ${SANS}`;
  ctx.fillText(PUJA_TIMETABLE_BN_TITLE, cx, 558);

  // Title pill
  ctx.font = `600 28px ${SANS}`;
  const label = "DATE & TIME TABLE";
  const labelW = trackedWidth(ctx, label, 5);
  const plateW = labelW + 96;
  const plateH = 60;
  const plateX = cx - plateW / 2;
  const plateY = 586;
  const plate = ctx.createLinearGradient(plateX, plateY, plateX, plateY + plateH);
  plate.addColorStop(0, "#F8E3B0");
  plate.addColorStop(1, "#DE9838");
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.35)";
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 8;
  roundRect(ctx, plateX, plateY, plateW, plateH, plateH / 2);
  ctx.fillStyle = plate;
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = "#4A1B06";
  drawTracked(ctx, label, cx, plateY + 39, 5, "center");
}

function drawEntry(ctx, entry, y) {
  const mid = y + ENTRY_H / 2;
  ctx.textBaseline = "middle";

  ctx.textAlign = "left";
  ctx.fillStyle = IVORY;
  ctx.font = `600 34px ${SERIF}`;
  ctx.fillText(entry.tithi, COL_X, mid);

  const right = COL_X + COL_W;
  if (entry.time == null) {
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(253,246,236,.40)";
    ctx.font = `400 34px ${SANS}`;
    ctx.fillText("—", right, mid);
  } else {
    const { clock, part } = splitTime(entry.time);
    let x = right;
    ctx.textAlign = "right";
    if (part.length > 0) {
      ctx.fillStyle = "rgba(253,246,236,.55)";
      ctx.font = `400 24px ${SANS}`;
      ctx.fillText(part, x, mid + 1);
      x -= ctx.measureText(part).width + 14;
    }
    ctx.fillStyle = GOLD_BRIGHT;
    ctx.font = `600 34px ${SANS}`;
    ctx.fillText(clock, x, mid);
    x -= ctx.measureText(clock).width + 16;
    ctx.fillStyle = "rgba(232,180,92,.70)";
    ctx.font = `600 18px ${SANS}`;
    drawTracked(ctx, "UPTO", x, mid + 1, 3, "right");
  }

  ctx.textBaseline = "alphabetic";
}

function drawDayCard(ctx, group, y) {
  const h = group.h;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.38)";
  ctx.shadowBlur = 26;
  ctx.shadowOffsetY = 10;
  const fill = ctx.createLinearGradient(0, y, 0, y + h);
  fill.addColorStop(0, "rgba(255,246,232,.13)");
  fill.addColorStop(1, "rgba(255,246,232,.05)");
  roundRect(ctx, CARD_X, y, CARD_W, h, CARD_R);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.restore();

  roundRect(ctx, CARD_X, y, CARD_W, h, CARD_R);
  ctx.strokeStyle = "rgba(232,180,92,.28)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Weekday + tithi rows, and the calendar tile heading them.
  const contentH = DAY_LABEL_H + group.entries.length * ENTRY_H;
  const top = y + (h - contentH) / 2;

  const d = splitDate(group.date);
  const tx = CARD_X + TILE_M;
  // Top-aligned so the date reads as the heading for every tithi under it.
  const ty = top;
  const tg = ctx.createLinearGradient(tx, ty, tx, ty + TILE);
  tg.addColorStop(0, "#F8E3B0");
  tg.addColorStop(1, "#DE9838");
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.32)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 6;
  roundRect(ctx, tx, ty, TILE, TILE, 24);
  ctx.fillStyle = tg;
  ctx.fill();
  ctx.restore();

  ctx.textAlign = "center";
  ctx.fillStyle = "#4A1B06";
  ctx.font = `700 54px ${SERIF}`;
  ctx.fillText(d.day, tx + TILE / 2, ty + 70);
  ctx.fillStyle = "rgba(74,27,6,.75)";
  ctx.font = `600 20px ${SANS}`;
  drawTracked(ctx, d.month, tx + TILE / 2, ty + 98, 3, "center");

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(248,227,176,.62)";
  ctx.font = `600 20px ${SANS}`;
  drawTracked(ctx, group.day.toUpperCase(), COL_X, top + 22, 4);

  let ey = top + DAY_LABEL_H;
  group.entries.forEach((entry, i) => {
    if (i > 0) {
      ctx.strokeStyle = "rgba(248,227,176,.16)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(COL_X, ey + 0.5);
      ctx.lineTo(COL_X + COL_W, ey + 0.5);
      ctx.stroke();
    }
    drawEntry(ctx, entry, ey);
    ey += ENTRY_H;
  });
}

function drawNote(ctx, y, lines, h) {
  roundRect(ctx, CARD_X, y, CARD_W, h, CARD_R);
  ctx.fillStyle = "rgba(232,180,92,.10)";
  ctx.fill();
  ctx.strokeStyle = "rgba(232,180,92,.34)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.textAlign = "left";
  ctx.fillStyle = GOLD;
  ctx.font = `600 20px ${SANS}`;
  drawTracked(ctx, "ON DASHAMI", CARD_X + 44, y + 48, 4);

  ctx.fillStyle = "rgba(253,246,236,.90)";
  ctx.font = `400 28px ${SANS}`;
  lines.forEach((line, i) => {
    ctx.fillText(line, CARD_X + 44, y + 98 + i * 40);
  });
}

function drawVenue(ctx, y, lines, h) {
  roundRect(ctx, CARD_X, y, CARD_W, h, CARD_R);
  ctx.fillStyle = "rgba(255,246,232,.08)";
  ctx.fill();
  ctx.strokeStyle = "rgba(232,180,92,.24)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  drawPin(ctx, CARD_X + 60, y + 44, 46, GOLD);

  const x = CARD_X + 108;
  ctx.textAlign = "left";
  ctx.fillStyle = GOLD;
  ctx.font = `600 18px ${SANS}`;
  drawTracked(ctx, "WHERE", x, y + 46, 4);

  ctx.fillStyle = IVORY;
  ctx.font = `600 32px ${SERIF}`;
  ctx.fillText(VENUE_NAME, x, y + 86);

  ctx.fillStyle = "rgba(253,246,236,.72)";
  ctx.font = `400 26px ${SANS}`;
  lines.forEach((line, i) => {
    ctx.fillText(line, x, y + 128 + i * 36);
  });
}

function drawFooter(ctx, y) {
  const cx = W / 2;
  drawDiamondRule(ctx, cx, y + 10, 200);

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(248,227,176,.80)";
  ctx.font = `600 22px ${SANS}`;
  drawTracked(ctx, `SORRBA  ·  DURGOTSAV ${PUJA_YEAR}`, cx, y + 68, 5, "center");

  ctx.fillStyle = "rgba(253,246,236,.70)";
  ctx.font = `400 26px ${SANS}`;
  ctx.fillText("Celebrating 18 years of togetherness", cx, y + 112);

  ctx.fillStyle = GOLD;
  ctx.font = `600 28px ${SANS}`;
  ctx.fillText("www.sorrba.org", cx, y + 158);
}

/* ---------- assets ---------- */

function loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = logoSrc;
  });
}

async function waitForFonts() {
  if (document.fonts != null && document.fonts.ready != null) {
    try {
      await document.fonts.ready;
    } catch (e) {
      /* fall back to system fonts */
    }
  }
}

/** Draws the poster and resolves with a PNG blob. */
export async function buildTimetableImage() {
  await waitForFonts();
  const logo = await loadLogo();

  const groups = groupByDate(PUJA_TIMETABLE);

  // Measuring pass, so the canvas height fits the wrapped copy exactly.
  const meter = document.createElement("canvas").getContext("2d");
  meter.font = `400 28px ${SANS}`;
  const noteLines = wrapText(meter, PUJA_DASHAMI_RITUALS, CARD_W - 88);
  meter.font = `400 26px ${SANS}`;
  const venueLines = wrapText(meter, VENUE_ADDRESS, CARD_W - 160);

  const noteH = 98 + noteLines.length * 40;
  const venueH = 104 + venueLines.length * 36 + 30;
  const groupsH =
    groups.reduce((sum, g) => sum + g.h, 0) + CARD_GAP * (groups.length - 1);
  const H = HEADER_H + groupsH + 24 + noteH + 20 + venueH + FOOTER_H + 30;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  paintBackground(ctx, H);
  drawHeader(ctx, logo);

  let y = HEADER_H;
  groups.forEach((group) => {
    drawDayCard(ctx, group, y);
    y += group.h + CARD_GAP;
  });
  y = y - CARD_GAP + 24;

  drawNote(ctx, y, noteLines, noteH);
  y += noteH + 20;

  drawVenue(ctx, y, venueLines, venueH);
  y += venueH;

  drawFooter(ctx, y);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob != null) {
        resolve(blob);
      } else {
        reject(new Error("Could not render the timetable image"));
      }
    }, "image/png");
  });
}

function downloadBlob(blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = FILE_NAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/** Renders the poster into a shareable File. Call ahead of the tap — see below. */
export async function prepareTimetableFile() {
  const blob = await buildTimetableImage();
  return new File([blob], FILE_NAME, { type: "image/png" });
}

function whatsAppFallback(file) {
  downloadBlob(file);
  window.open(
    `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`,
    "_blank",
    "noopener,noreferrer"
  );
  return "downloaded";
}

/**
 * Shares the poster. Resolves with:
 *   "shared"     handed to the device share sheet
 *   "cancelled"  user dismissed the share sheet
 *   "downloaded" no file-share support — image saved and WhatsApp opened
 *
 * Pass a file prepared earlier (see prepareTimetableFile): navigator.share needs
 * the user gesture to still be live, and rendering the canvas first burns it —
 * iOS Safari rejects the call outright. With the file ready, share() is reached
 * before the first await, so the tap still counts.
 */
export async function shareTimetable(preparedFile) {
  const file = preparedFile != null ? preparedFile : await prepareTimetableFile();

  if (navigator.canShare != null && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `SORRBA · Durga Puja ${PUJA_YEAR}`,
        text: SHARE_TEXT,
      });
      return "shared";
    } catch (e) {
      if (e != null && e.name === "AbortError") {
        return "cancelled";
      }
      // Share sheet refused the file — save it and open WhatsApp instead.
      return whatsAppFallback(file);
    }
  }

  return whatsAppFallback(file);
}
